import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource, PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import ScopedCommonTemplateScopeEdit from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedCommonTemplateScopeEdit";
import type { CommonTemplatesChangeTracker, CommonTemplatesKey } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import { EnvironmentChip } from "~/components/Chips";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import { Text } from "~/components/form/index";
import ChangedValueIndicator from "../ChangedValueIndicator";
interface ScopedCommonTemplateScopeCellProps {
    environments: EnvironmentResource[];
    changeTracker: CommonTemplatesChangeTracker;
    scopeKey: CommonTemplatesKey;
    focused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    hasEditPermissions: boolean;
    canBeScoped: boolean;
    hasScopeChanged: boolean;
    initialEnvironmentScope: string[];
    initialValue: PropertyValueResource | undefined;
}
export default ({ environments, changeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, canBeScoped, hasScopeChanged, initialEnvironmentScope, initialValue }: ScopedCommonTemplateScopeCellProps) => (<ScopedCommonTemplateScopeEditableCell environments={environments} changeTracker={changeTracker} scopeKey={scopeKey} focused={focused} onFocus={onFocus} onBlur={onBlur} hasEditPermissions={hasEditPermissions} canBeScoped={canBeScoped} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={initialEnvironmentScope} initialValue={initialValue}/>);
interface ScopedCommonTemplateScopeEditableCellProps extends ScopedCommonTemplateScopeCellProps {
    hasEditPermissions: boolean;
}
function ScopedCommonTemplateScopeEditableCell({ environments, changeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, canBeScoped, hasScopeChanged, initialEnvironmentScope, initialValue }: ScopedCommonTemplateScopeEditableCellProps) {
    const calculateFocusedPopoverPosition = (): PopoverPosition => {
        return {
            top: 0,
            left: 0,
            right: 0,
        };
    };
    if (!hasEditPermissions || !canBeScoped) {
        return (<div className={cellStyles.container}>
                <div className={cellStyles.readonlyContainer}>
                    <ScopedCommonTemplateReadonlyCell environments={environments} scopeKey={scopeKey} changeTracker={changeTracker} initialEnvironmentScope={initialEnvironmentScope} hasEditPermissions={hasEditPermissions} canBeScoped={canBeScoped} hasScopeChanged={hasScopeChanged}/>
                </div>
            </div>);
    }
    return (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.container}>
            <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} dynamicPosition={calculateFocusedPopoverPosition}>
                {focused ? (<ScopedCommonTemplateScopeEdit scopeKey={scopeKey} changeTracker={changeTracker} initialEnvironmentScope={initialEnvironmentScope} initialValue={initialValue} environments={environments}/>) : (<div className={cellStyles.readonlyContainer}>
                        <ScopedCommonTemplateReadonlyCell environments={environments} scopeKey={scopeKey} changeTracker={changeTracker} initialEnvironmentScope={initialEnvironmentScope} hasEditPermissions={hasEditPermissions} canBeScoped={canBeScoped} hasScopeChanged={hasScopeChanged}/>
                        <div className={cellStyles.changedValueIndicator}>
                            <ChangedValueIndicator show={hasScopeChanged}/>
                        </div>
                    </div>)}
            </PopoverWhenFocused>
        </div>);
}
interface ScopedCommonTemplateScopeReadonlyCellProps {
    environments: EnvironmentResource[];
    scopeKey: CommonTemplatesKey;
    changeTracker: CommonTemplatesChangeTracker;
    initialEnvironmentScope: string[];
    hasEditPermissions: boolean;
    canBeScoped: boolean;
    hasScopeChanged: boolean;
}
const ScopedCommonTemplateReadonlyCell = (props: ScopedCommonTemplateScopeReadonlyCellProps) => {
    const { environments, scopeKey, changeTracker, initialEnvironmentScope, canBeScoped } = props;
    const environmentMap = new Map<string, EnvironmentResource>();
    environments.forEach((e) => environmentMap.set(e.Id, e));
    const trackedEntry = changeTracker.has(scopeKey) ? changeTracker.get(scopeKey) : undefined;
    const scope = trackedEntry ? trackedEntry.environmentIds : initialEnvironmentScope;
    const scopeLength = scope?.length ?? 0;
    const tooManyChips = scopeLength >= 3;
    if (!scope?.length) {
        return <Text value={""} placeholder={canBeScoped ? "Define Scope" : undefined} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true} disabled={!canBeScoped}/>;
    }
    return (<div tabIndex={0}>
            {tooManyChips ? (<EnvironmentChip environmentName={`${scopeLength} environments`} key="environmentsChipsList" description={scope.map((s) => environmentMap.get(s)?.Name).join(", ")}/>) : (<>
                    {scope.map((s) => {
                const environment = environmentMap.get(s);
                return environment ? <EnvironmentChip environmentName={environment.Name} key={environment.Id}/> : null;
            })}
                </>)}
        </div>);
};
const cellStyles = {
    container: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    changedValueIndicator: css({
        marginLeft: "auto",
    }),
    readonlyContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} 0`,
    }),
};
